import { Message } from "../types/Types";

const MESSAGE_READ_STATE_PREFIX = "header.messageReadState";
export const HEADER_MESSAGE_READ_EVENT = "header-message-read-state";

type ConversationLike = {
  id: number;
  messages: Message[];
};

export type MessageReadState = Record<string, string>;

function storageKey(objectId: string): string {
  return `${MESSAGE_READ_STATE_PREFIX}.${objectId}`;
}

function toTimestamp(value?: string | null): number {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function loadMessageReadState(objectId: string): MessageReadState {
  if (!objectId) return {};

  try {
    const raw = localStorage.getItem(storageKey(objectId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function markConversationAsRead(
  objectId: string,
  conversationId: number,
  lastIncomingDateSent?: string | null
): void {
  if (!objectId || !conversationId || !lastIncomingDateSent) return;

  const currentState = loadMessageReadState(objectId);
  const key = String(conversationId);
  const currentTs = toTimestamp(currentState[key]);
  const nextTs = toTimestamp(lastIncomingDateSent);

  if (nextTs <= currentTs) return;

  const nextState: MessageReadState = {
    ...currentState,
    [key]: lastIncomingDateSent,
  };

  localStorage.setItem(storageKey(objectId), JSON.stringify(nextState));
  window.dispatchEvent(
    new CustomEvent(HEADER_MESSAGE_READ_EVENT, {
      detail: { objectId },
    })
  );
}

export function getLastIncomingMessageDate(
  conversation: ConversationLike,
  currentUserId?: number
): string | null {
  if (!currentUserId) return null;

  const incomingMessages = conversation.messages.filter(
    (message) => Number(message.userIdMessageFrom) !== Number(currentUserId)
  );

  return incomingMessages[incomingMessages.length - 1]?.dateSent ?? null;
}

export function getConversationUnreadCount(
  conversation: ConversationLike,
  currentUserId: number | undefined,
  readState: MessageReadState
): number {
  if (!currentUserId) return 0;

  const readAtTs = toTimestamp(readState[String(conversation.id)]);

  return conversation.messages.filter((message) => {
    if (Number(message.userIdMessageFrom) === Number(currentUserId)) return false;
    return toTimestamp(message.dateSent) > readAtTs;
  }).length;
}

export function getUnreadMessageCount(
  conversations: ConversationLike[],
  currentUserId: number | undefined,
  readState: MessageReadState
): number {
  return conversations.reduce(
    (total, conversation) =>
      total + getConversationUnreadCount(conversation, currentUserId, readState),
    0
  );
}
